import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  ArrowLeft,
  Plus,
  Minus,
  Trash2,
  ShoppingBag,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { menuData, parseSizes } from '../mock';
import { useCart } from '../context/CartContext';
import { api, TURNSTILE_SITE_KEY, getDeviceId } from '../lib/api';

const OptionSelect = ({ label, value, options, onChange }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] uppercase tracking-wider text-[#7a5c40]">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#f4ead4] border border-[#2a1a0f]/15 rounded-lg px-3 py-2 text-[13px] text-[#2a1a0f] focus:outline-none focus:border-[#b8532a] cursor-pointer"
    >
      {options.map((o) =>
        typeof o === 'string' ? (
          <option key={o} value={o}>
            {o}
          </option>
        ) : (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        )
      )}
    </select>
  </label>
);

const MenuItemRow = ({ item, category, addToCart, findLine, updateQuantity }) => {
  const sizes = parseSizes(item.price);
  const flavors = item.flavors;
  const toppings = item.toppings;
  const styles = item.styles;
  const addons = item.addons;
  const hasOptions = !!(sizes || flavors || toppings || styles || addons);

  const [size, setSize] = useState(sizes ? sizes[0].label : '');
  const [flavor, setFlavor] = useState(flavors ? flavors[0] : '');
  const [topping, setTopping] = useState(toppings ? toppings[0] : '');
  const [style, setStyle] = useState(styles ? styles[0] : '');
  const [addon, setAddon] = useState(addons ? addons[0] : '');

  const currentSizePriceStr = sizes
    ? sizes.find((s) => s.label === size)?.price || item.price
    : item.price;

  // Compose "flavor" field on line to store all variant choices in one place
  const variantParts = [flavor, topping, style, addon && addon !== 'No add-on' ? addon : '']
    .filter(Boolean);
  const variantValue = variantParts.join(' · ');

  const inCart = findLine(item.name, category, size, variantValue);

  const doAdd = () => {
    addToCart(item, category, {
      size,
      sizePriceStr: currentSizePriceStr,
      flavor: variantValue,
    });
  };

  // Chip preview – show up to 5 of the primary option list on the row
  const chipList = flavors || toppings || styles || (addons && addons.filter((a) => a !== 'No add-on'));

  return (
    <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-[#2a1a0f]">{item.name}</div>
        <div className="text-[13px] text-[#b8532a] mt-0.5">
          {sizes
            ? sizes.map((s) => `${s.label} ${s.price}`).join(' · ')
            : item.price}
        </div>
        
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        {sizes && (
          <OptionSelect
            label="Size"
            value={size}
            options={sizes.map((s) => ({ value: s.label, label: `${s.label}` }))}
            onChange={setSize}
          />
        )}
        {flavors && (
          <OptionSelect label="Flavour" value={flavor} options={flavors} onChange={setFlavor} />
        )}
        {toppings && (
          <OptionSelect label="Topping" value={topping} options={toppings} onChange={setTopping} />
        )}
        {styles && (
          <OptionSelect label="Style" value={style} options={styles} onChange={setStyle} />
        )}
        {addons && (
          <OptionSelect label="Add-on" value={addon} options={addons} onChange={setAddon} />
        )}

        {inCart ? (
          <div className="inline-flex items-center gap-3 bg-[#2a1a0f] text-[#f4ead4] rounded-full px-2 py-1.5 self-end">
            <button
              onClick={() => updateQuantity(inCart, -1)}
              className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="decrease"
            >
              <Minus className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <span className="text-[14px] min-w-[16px] text-center">{inCart.quantity}</span>
            <button
              onClick={doAdd}
              className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="increase"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <button
            onClick={doAdd}
            className={`inline-flex items-center gap-1.5 bg-[#f4ead4] hover:bg-[#ecdcbf] border border-[#2a1a0f]/15 text-[#2a1a0f] px-4 py-2 rounded-full text-[13px] transition-colors ${
              hasOptions ? 'self-end' : ''
            }`}
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add
          </button>
        )}
      </div>
    </li>
  );
};

const PreorderPage = () => {
  const navigate = useNavigate();
  const {
    cart,
    addToCart,
    updateQuantity,
    removeLine,
    findLine,
    clearCart,
    totalItems,
    totalPrice,
  } = useCart();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [placed, setPlaced] = useState(false);
  const [orderId, setOrderId] = useState('');
  const submitLockRef = useRef(false);
  const turnstileRef = useRef(null);

  const categories = Object.entries(menuData);

  const buildOptionSuffix = (c) => {
    const bits = [];
    if (c.size) bits.push(c.size);
    if (c.flavor) bits.push(c.flavor);
    return bits.length ? ` — ${bits.join(', ')}` : '';
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || submitting) return;
    if (!cart.length || !name.trim() || !phone.trim()) return;
    if (!turnstileToken) {
      setError('Please complete the CAPTCHA below.');
      return;
    }
    submitLockRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        customerName: name.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
        items: cart.map((c) => ({
          name: `${c.name}${buildOptionSuffix(c)}`,
          priceStr: c.priceStr,
          price: c.price,
          category: c.category,
          quantity: c.quantity,
        })),
        total: Number(totalPrice.toFixed(2)),
        turnstileToken,
        website,
        deviceId: getDeviceId(),
      };
      const { data } = await api.post('/orders', payload);
      clearCart();
      setName('');
      setPhone('');
      setNotes('');
      setOrderId(data.id);
      setPlaced(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (typeof detail === 'string' && detail.trim()) {
        msg = detail;
      } else if (status === 422) {
        msg = 'Please double-check your name, phone, and cart before submitting.';
      } else if (status === 429) {
        msg = 'Too many attempts. Please wait a moment and try again.';
      } else if (status === 400) {
        msg = 'Please complete the CAPTCHA and try again.';
      } else {
        msg = "Couldn't place your order — please try again or call (250) 992-7066.";
      }
      setError(msg);
      try {
        turnstileRef.current?.reset();
      } catch {
        /* ignore */
      }
      setTurnstileToken('');
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        submitLockRef.current = false;
      }, 1500);
    }
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-[#f4ead4] flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full bg-[#ecdcbf] rounded-3xl p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[#b8532a] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#f4ead4]" strokeWidth={1.8} />
          </div>
          <h1 className="font-serif text-[38px] leading-tight text-[#2a1a0f]">Order placed!</h1>
          <p className="mt-3 text-[15px] text-[#4a3524]">
            Order <span className="font-medium">{orderId}</span> is in. The shop will call you at
            pickup time to confirm.
          </p>
          <div className="mt-8 flex items-center gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate('/')}
              className="bg-[#2a1a0f] hover:bg-[#3d2817] text-[#f4ead4] px-6 py-3 rounded-full text-[15px] transition-colors"
            >
              Back to home
            </button>
            <button
              onClick={() => setPlaced(false)}
              className="bg-[#f4ead4] border border-[#2a1a0f]/15 text-[#2a1a0f] px-6 py-3 rounded-full text-[15px] hover:bg-[#f4ead4]/70 transition-colors"
            >
              Place another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4ead4]">
      <header className="sticky top-0 z-50 bg-[#f4ead4]/95 backdrop-blur-md border-b border-[#2a1a0f]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#2a1a0f] hover:opacity-70 transition">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            <span className="text-[15px]">Back</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#2a1a0f] flex items-center justify-center">
              <span className="text-[#f4ead4] font-serif text-lg">B</span>
            </div>
            <span className="font-serif text-[19px] text-[#2a1a0f]">Bliss Coffee</span>
          </div>
          <a href="#cart" className="relative inline-flex items-center gap-2 bg-[#2a1a0f] text-[#f4ead4] px-4 py-2 rounded-full">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
            <span className="text-[14px]">{totalItems}</span>
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="text-[12px] tracking-[0.16em] font-medium text-[#b8532a] uppercase mb-4">
          Pre-order for pickup
        </div>
        <h1 className="font-serif text-[44px] sm:text-[56px] leading-[1.05] text-[#2a1a0f] tracking-tight">
          Build your order.
        </h1>
        <p className="mt-4 text-[16px] text-[#4a3524] max-w-2xl">
          Pick your size and flavour where available, then drop your name and phone. Pay at pickup.
        </p>

        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-10">
          {/* Menu list */}
          <div>
            {categories.map(([key, cat]) => (
              <div key={key} className="mb-12">
                <div className="flex items-center gap-4 pb-4 border-b border-[#2a1a0f]/15">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img src={cat.image} alt={cat.title} className="w-full h-full object-cover" />
                  </div>
                  <h2 className="font-serif text-[24px] text-[#2a1a0f]">{cat.title}</h2>
                </div>
                <ul className="divide-y divide-[#2a1a0f]/10">
                  {cat.items.map((item, i) => (
                    <MenuItemRow
                      key={i}
                      item={item}
                      category={cat.title}
                      addToCart={addToCart}
                      findLine={findLine}
                      updateQuantity={updateQuantity}
                    />
                  ))}
                </ul>
                {cat.footnote && (
                  <p className="mt-4 text-[12px] italic text-[#7a5c40]">{cat.footnote}</p>
                )}
              </div>
            ))}
          </div>

          {/* Cart / checkout */}
          <div id="cart" className="lg:sticky lg:top-24 h-fit">
            <div className="bg-[#ecdcbf] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <ShoppingBag className="w-5 h-5 text-[#2a1a0f]" strokeWidth={1.8} />
                <h3 className="font-serif text-[22px] text-[#2a1a0f]">Your cart</h3>
              </div>

              {cart.length === 0 ? (
                <p className="text-[14px] text-[#7a5c40] py-6">
                  Your cart is empty. Add something warm to get started.
                </p>
              ) : (
                <>
                  <ul className="divide-y divide-[#2a1a0f]/10 mb-4">
                    {cart.map((c, i) => (
                      <li key={i} className="py-3 flex items-center gap-3">
                        <div className="flex-1">
                          <div className="text-[14px] text-[#2a1a0f] leading-snug">
                            {c.name}
                            {buildOptionSuffix(c) && (
                              <span className="text-[#7a5c40]">{buildOptionSuffix(c)}</span>
                            )}
                          </div>
                          <div className="text-[12px] text-[#7a5c40] mt-0.5">{c.priceStr}</div>
                        </div>
                        <div className="inline-flex items-center gap-2 bg-[#f4ead4] rounded-full px-1.5 py-1">
                          <button
                            onClick={() => updateQuantity(c, -1)}
                            className="w-6 h-6 rounded-full hover:bg-[#2a1a0f]/10 flex items-center justify-center"
                          >
                            <Minus className="w-3 h-3" strokeWidth={2} />
                          </button>
                          <span className="text-[13px] min-w-[14px] text-center">{c.quantity}</span>
                          <button
                            onClick={() => updateQuantity(c, +1)}
                            className="w-6 h-6 rounded-full hover:bg-[#2a1a0f]/10 flex items-center justify-center"
                          >
                            <Plus className="w-3 h-3" strokeWidth={2} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeLine(c)}
                          className="text-[#7a5c40] hover:text-[#b8532a] p-1"
                          aria-label="remove"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.8} />
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between py-3 border-t border-[#2a1a0f]/15">
                    <span className="text-[15px] text-[#2a1a0f]">Estimated subtotal</span>
                    <span className="font-serif text-[22px] text-[#2a1a0f]">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="bg-[#f4ead4]/70 border border-[#2a1a0f]/10 rounded-xl px-4 py-3 mb-3">
                    <p className="text-[12px] text-[#4a3524] leading-relaxed">
                      <span className="font-medium text-[#2a1a0f]">Please note:</span> Taxes are
                      not included above and will be calculated at pickup checkout.
                    </p>
                  </div>

                  <form onSubmit={placeOrder} className="space-y-3">
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      maxLength={80}
                      className="w-full bg-[#f4ead4] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a] transition-colors"
                    />
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Phone number"
                      maxLength={20}
                      className="w-full bg-[#f4ead4] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a] transition-colors"
                    />
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notes (allergies, extras, pickup time…)"
                      rows={3}
                      maxLength={500}
                      className="w-full bg-[#f4ead4] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a] transition-colors resize-none"
                    />

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      style={{
                        position: 'absolute',
                        left: '-10000px',
                        top: 'auto',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        opacity: 0,
                      }}
                      aria-hidden="true"
                    />

                    <div className="flex justify-center">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        options={{ theme: 'light', size: 'flexible' }}
                        onSuccess={setTurnstileToken}
                        onError={() => setTurnstileToken('')}
                        onExpire={() => setTurnstileToken('')}
                      />
                    </div>

                    {error && (
                      <div className="flex items-start gap-2 bg-[#f8d7d0] border border-[#b8532a]/30 text-[#8a3312] rounded-xl px-4 py-3 text-[13px]">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !turnstileToken}
                      className="w-full bg-[#b8532a] hover:bg-[#a34620] disabled:bg-[#b8532a]/50 disabled:cursor-not-allowed text-[#f4ead4] py-3.5 rounded-full text-[15px] font-medium transition-colors inline-flex items-center justify-center gap-2"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? 'Placing order…' : 'Place order · Pay at pickup'}
                    </button>
                  </form>
                </>
              )}
            </div>

            <p className="mt-4 text-[12px] text-[#7a5c40] leading-relaxed">
              Payment happens in-store when you pick up your order.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreorderPage;
